// Same-origin: all /api/* calls route through the Cloudflare Pages Function
// (functions/api/[[route]].js), which proxies to Railway with the service
// token injected. This avoids CORS and lets CF Access edge-inject staff
// identity headers at work.squish.co before the Pages Function runs.
//
// Local dev: CRA's dev server won't have the Pages Function — point to the
// crm-api.squish.co origin directly and rely on CF_Authorization cookie
// passthrough (needs you to have logged into work.squish.co first in the
// same browser so the cookie is set on .squish.co).
const BASE = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

import { accountTokensHeader } from './lib/accountSession';

export function getHeaders(extra = {}) {
  const cfToken = document.cookie
    .split('; ')
    .find(row => row.startsWith('CF_Authorization='))
    ?.split('=')[1];
  const headers = { 'Content-Type': 'application/json', ...extra };
  if (cfToken) headers['cf-access-jwt-assertion'] = cfToken;
  // Every OTP token this tab holds. Attached to ALL calls rather than threaded
  // through the ~20 account-scoped helpers individually: the account-scoped
  // routes each check that their own account is in the set, and the global ~
  // console — which belongs to no single account — scopes on the same set.
  // Threading it per call site was how the boundary ended up enforced on one
  // route out of a dozen.
  const accountTokens = accountTokensHeader();
  if (accountTokens) headers['x-account-tokens'] = accountTokens;
  return headers;
}

export async function fetchCases() {
  const res = await fetch(`${BASE}/api/cases`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Failed to fetch cases');
  return res.json();
}

// Who is logged in (CF Access JWT → Salesforce User). Drives the sidebar
// footer so it reflects the actual staff member, not a hardcoded name.
export async function fetchWhoami() {
  const res = await fetch(`${BASE}/api/whoami`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Failed to resolve current user');
  return res.json();
}

export async function searchCustomers(q) {
  const res = await fetch(`${BASE}/api/search?q=${encodeURIComponent(q)}`, { headers: getHeaders() });
  if (!res.ok) {
    // Pull the upstream error body (if JSON) so the diagnostic surfaces what
    // actually failed — Salesforce auth, CF Access, missing service token,
    // etc. Without this we previously threw a generic "Failed to search" and
    // the UI swallowed it, leaving the staff member staring at the initial
    // "Start typing..." state with no signal that anything went wrong.
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error || JSON.stringify(body);
    } catch {
      try { detail = await res.text(); } catch { /* nothing usable */ }
    }
    const err = new Error(`Search failed (${res.status}): ${detail || res.statusText || 'unknown error'}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

// ─── Docs ────────────────────────────────────────────────────────────────────
export async function fetchDocs(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/api/docs${qs ? '?' + qs : ''}`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Failed to fetch docs');
  return res.json();
}

export async function createDoc(doc) {
  const res = await fetch(`${BASE}/api/docs`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(doc),
  });
  if (!res.ok) throw new Error('Failed to create doc');
  return res.json();
}

export async function updateDoc(id, updates) {
  const res = await fetch(`${BASE}/api/docs/${id}`, {
    method: 'PATCH', headers: getHeaders(), body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error('Failed to update doc');
  return res.json();
}

export async function deleteDoc(id) {
  const res = await fetch(`${BASE}/api/docs/${id}`, {
    method: 'DELETE', headers: getHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete doc');
  return res.json();
}

export async function uploadDocFile(formData) {
  const cfToken = document.cookie
    .split('; ')
    .find(row => row.startsWith('CF_Authorization='))
    ?.split('=')[1];
  const headers = {};
  if (cfToken) headers['cf-access-jwt-assertion'] = cfToken;
  const res = await fetch(`${BASE}/api/docs/upload`, {
    method: 'POST', headers, body: formData,
  });
  if (!res.ok) throw new Error('Failed to upload file');
  return res.json();
}

export function getDocFileUrl(docId) {
  return `${BASE}/api/docs/file/${docId}`;
}

// Live assistant reflection — the ACTUAL prompt the FOS assistant runs (built
// in squish-backend code) plus the LLM changelog. Read-only. See the
// Assistant page.
export async function fetchAssistantReflection() {
  const res = await fetch(`${BASE}/api/assistant/reflection`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Failed to fetch live assistant prompt');
  return res.json();
}

// The ~ console agent (Chuck): prompt, model settings and tool catalog, read
// from squish-crm-backend lib/console-agent.js. Read-only.
export async function fetchConsoleReflection() {
  const res = await fetch(`${BASE}/api/console/reflection`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Failed to fetch the console prompt');
  return res.json();
}

// ─── M365 tenant + DNS sync ─────────────────────────────────────────────────
// Throws an Error with a `.status` property set to the HTTP status so UI code
// can distinguish 424 (GDAP/auth) from 400/500 (other errors) for nicer messages.
async function dnsFetch(path, init = {}) {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: getHeaders(init.headers) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || body.detail || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export function saveM365TenantId(accountId, tenantId) {
  return dnsFetch(`/api/accounts/${accountId}/m365-tenant`, {
    method: 'PATCH',
    body: JSON.stringify({ tenantId: tenantId || null }),
  });
}

export function dnsSyncPreview(accountId, domain, { tenantId } = {}) {
  const qs = new URLSearchParams({ domain });
  if (tenantId) qs.set('tenantId', tenantId);
  return dnsFetch(`/api/accounts/${accountId}/dns-sync/preview?${qs}`);
}

export function dnsSyncWriteServices(accountId, { domain, tenantId, services, skipOptional } = {}) {
  return dnsFetch(`/api/accounts/${accountId}/dns-sync/services`, {
    method: 'POST',
    body: JSON.stringify({ domain, tenantId, services, skipOptional }),
  });
}

export function dnsSyncWriteVerify(accountId, { domain, tenantId, triggerVerify } = {}) {
  return dnsFetch(`/api/accounts/${accountId}/dns-sync/verify`, {
    method: 'POST',
    body: JSON.stringify({ domain, tenantId, triggerVerify }),
  });
}

// ─── Carts ───────────────────────────────────────────────────────────────────
// Same error-raising pattern as dnsFetch: Error.status carries the HTTP code.
async function cartFetch(path, init = {}) {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: getHeaders(init.headers) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export function listAccountCarts(accountId, { status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return cartFetch(`/api/accounts/${accountId}/carts${qs}`);
}

export function createAccountCart(accountId, { items, notes } = {}) {
  return cartFetch(`/api/accounts/${accountId}/carts`, {
    method: 'POST',
    body: JSON.stringify({ items, notes }),
  });
}

export function createProspectCart({ prospect, items, notes } = {}) {
  return cartFetch('/api/prospect-carts', {
    method: 'POST',
    body: JSON.stringify({ prospect, items, notes }),
  });
}

// A rep invites a new customer with the order attached: squish-backend makes
// their account, the cart and a single-use link, and emails it from the rep.
// staffOverride is the same cookie-peek fallback shareCart sends (defined
// below; hoisted).
export function createProspectInvite({ prospect, items, notes } = {}) {
  return cartFetch('/api/prospect-invites', {
    method: 'POST',
    body: JSON.stringify({ prospect, items, notes, staffOverride: cfStaffOverride() }),
  });
}

export function listProspectInvites({ limit } = {}) {
  const qs = limit ? `?limit=${encodeURIComponent(limit)}` : '';
  return cartFetch(`/api/prospect-invites${qs}`);
}

// sendEmail:false mints a fresh link for the rep to copy without mailing it.
// Either way the previous link stops working.
export function resendProspectInvite(cartId, { sendEmail = true } = {}) {
  return cartFetch(`/api/prospect-invites/${encodeURIComponent(cartId)}/resend`, {
    method: 'POST',
    body: JSON.stringify({ sendEmail, staffOverride: cfStaffOverride() }),
  });
}

export function cancelCart(cartId) {
  return cartFetch(`/api/carts/${cartId}`, { method: 'DELETE' });
}

// Price cart lines exactly the way checkout will; nothing is saved. Returns
// { items, totalCents }: the normalized lines in the order sent, each with its
// server unitPrice. A refusal (409 PRICE_UNAVAILABLE) throws with
// squish-backend's reason, written for the rep. See src/lib/cartQuote.js.
export function quoteCartLines({ items, accountId, customerEmail, prospect } = {}) {
  return cartFetch('/api/carts/quote', {
    method: 'POST',
    body: JSON.stringify({ items, accountId, customerEmail, prospect }),
  });
}

// ─── Enterprise invoicing (sales-led deals) ─────────────────────────────────
// Three separate calls on purpose — see squish-backend/lib/enterprise-invoice.js.
// quoteEnterpriseDeal is pure and safe to call as the rep types; draft creates
// something private; send is the only one the customer ever sees. Never wrap
// draft+send into one convenience helper.

// Pure maths. Returns { ok, lines[], totalCents, errors[] } — a refused deal is
// a normal 200 with ok:false, not an error to catch.
export function quoteEnterpriseDeal({ lines, termMonths, netDays } = {}) {
  return cartFetch('/api/enterprise-invoice/quote', {
    method: 'POST',
    body: JSON.stringify({ lines, termMonths, netDays }),
  });
}

// Creates a Stripe DRAFT. Nothing reaches the customer.
export function draftEnterpriseInvoice({ customerEmail, customerName, accountId, lines, termMonths, netDays, poNumber, description } = {}) {
  return cartFetch('/api/enterprise-invoice/draft', {
    method: 'POST',
    body: JSON.stringify({ customerEmail, customerName, accountId, lines, termMonths, netDays, poNumber, description }),
  });
}

// OUTWARD-FACING: finalises the draft and emails the customer.
export function sendEnterpriseInvoice(invoiceId) {
  return cartFetch(`/api/enterprise-invoice/${encodeURIComponent(invoiceId)}/send`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// Best-effort peek at the CF Access JWT body to populate the "from" name/email
// on the share request. NOT used for authorization — the server still verifies
// independently. This is a fallback for when CF_ACCESS_TEAM_DOMAIN / AUD
// aren't set on the Railway service yet (pre-launch config).
function peekCfAccessClaims() {
  try {
    const jwt = document.cookie
      .split('; ')
      .find(r => r.startsWith('CF_Authorization='))
      ?.split('=')[1];
    if (!jwt) return null;
    const [, payload] = jwt.split('.');
    if (!payload) return null;
    // base64url → base64 → JSON
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch { return null; }
}

function cfStaffOverride() {
  const claims = peekCfAccessClaims();
  return claims?.email
    ? { email: claims.email, name: claims.name || claims.custom?.name || null }
    : null;
}

export function shareCart(cartId, { accountId, sendEmail = true, toEmail } = {}) {
  const staffOverride = cfStaffOverride();
  return cartFetch(`/api/carts/${cartId}/share`, {
    method: 'POST',
    body: JSON.stringify({ accountId, sendEmail, toEmail, staffOverride }),
  });
}

// ─── Domain ops ─────────────────────────────────────────────────────────────
export async function domainCheck(domain) {
  const res = await fetch(`${BASE}/api/domain-check?domain=${encodeURIComponent(domain)}`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// comp: true books the transfer as complimentary (a $0 Order credited to no
// rep; the registrar cost shows as negative margin). The customer is never
// charged on this route either way.
// Domain Manage panel (Loot -> Products -> a domain row). Runs one of the
// customer assistant's domain tools on this account as the signed-in staffer;
// the backend allowlists the tool, checks the domain is on this account and
// records every applied change in the account's Events timeline.
// Returns the tool envelope { ok, result, error, errorCode }.
// "Go live": give a placeholder hosting site the customer's real domain.
// The backend checks the site's owner is on this account and that the domain is
// theirs to use, then renames the site, points DNS and issues SSL (slow).
// Returns { success, domain, previousDomain, aRecords, externalRegistrar }.
export async function setHostingDomain(accountId, placeholder, domain) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/hosting/${encodeURIComponent(placeholder)}/set-domain`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ domain }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// "Set up": finish a paid hosting or email purchase the customer never set up.
// The backend runs the customer's own wizard submit as the setup's owner and
// logs it against the staffer. Slow: it builds the site / places the order.
export async function submitPendingSetup(accountId, kind, setupId, fields) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/pending-setups/${encodeURIComponent(kind)}/${encodeURIComponent(setupId)}/submit`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(fields || {}),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function domainAction(accountId, name, args = {}, { confirmed = false } = {}) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/domain-actions`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ name, args, confirmed }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function transferDomain(accountId, { domain, eppCode, contactInfo, comp = false } = {}) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/transfer-domain`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ domain, eppCode, contactInfo, comp: comp === true }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Customer-initiated transfers (staff read) ──────────────────────────────
// A DIFFERENT flow from transferDomain() above. That one is rep-driven and goes
// straight to NameSilo. These read the customer's own transfers out of
// squish-backend's domain_transfers collection — the ones started from the
// dashboard or at checkout, which is where transfers actually fail.
//
// The customer sees a mapped, deliberately generic failure line; the raw
// registrar message is staff-only and arrives as `lastError`. Both come back
// together so a rep can answer "it says contact support — why?".
export async function fetchAccountDomainTransfers(accountId) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/domain-transfers`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Add the auth code to a transfer this account bought in a cart without one,
// and submit it to the registrar. Same single-use claim as the customer's
// dashboard, so a double-click cannot buy it twice. A registrar refusal comes
// back as the thrown message (e.g. a wrong code) and the transfer stays
// awaiting its code.
export async function addTransferEpp(accountId, transferId, eppCode) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/domain-transfers/${encodeURIComponent(transferId)}/epp`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ eppCode }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Every attempt for one domain, for when a customer writes in with nothing but
// the domain name. Attempts are per PaymentIntent, so a retried purchase has
// more than one row and the older ones carry the earlier errors.
export async function fetchTransfersByDomain(domain) {
  const res = await fetch(`${BASE}/api/domain-transfers/by-domain/${encodeURIComponent(domain)}`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Ask NameSilo what it currently thinks about one transfer: is the domain
// already ours, is an order in flight, or is there nothing there? Returns a
// verdict plus a plain-language recommendation.
//
// ADVISORY — it changes nothing. Clearing a frozen transfer's claim is still a
// deliberate manual step, because releasing it re-arms a billable registrar
// call and a wrong release buys the domain twice.
export async function fetchTransferRegistrarStatus(transferId) {
  const res = await fetch(`${BASE}/api/domain-transfers/${encodeURIComponent(transferId)}/registrar-status`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Cross-account queue of transfers frozen mid-submission — a held registrar
// claim on a record still awaiting its EPP. Nothing moves these along on its
// own; they wait for a human.
export async function fetchTransfersNeedingAttention({ limit = 50 } = {}) {
  const res = await fetch(`${BASE}/api/domain-transfers/needs-attention?limit=${limit}`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Contacts ───────────────────────────────────────────────────────────────
export async function createContact(accountId, contact) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/contacts`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(contact),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Edit comms preferences on a SF Contact. Payload accepts only the four
// preference checkboxes — backend whitelist enforces this too.
export async function updateContactPreferences(contactId, prefs) {
  const res = await fetch(`${BASE}/api/contacts/${encodeURIComponent(contactId)}/preferences`, {
    method: 'PATCH', headers: getHeaders(), body: JSON.stringify(prefs || {}),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Opportunity stage + lost-deal reasons ─────────────────────────────────
// Deals were render-only in this CRM until 2026-08-03 — stage changes happened
// in the Salesforce UI, so there was nowhere here to ask WHY a deal died. That
// gap is what these three calls close: the pricing decisions Casey parked
// (multi-year discount ladder, entry-level VPS in the $15-35 band) stay parked
// until the lost-reason split shows entry price is actually what costs us.

// Valid stages come from the ORG (SF describe), not a literal in this file —
// stages are editable in Setup and a hardcoded copy here would be the thing
// that goes stale. Also returns the lost-reason picklist so the modal's
// options and the server's validation can't disagree.
export async function fetchOpportunityStages() {
  const res = await fetch(`${BASE}/api/opportunities/stages`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Closing a deal lost REQUIRES reason. The server rejects it without one, and
// a Salesforce validation rule rejects it even if this call is bypassed.
export async function updateOpportunityStage(oppId, { StageName, reason = null, notes = null } = {}) {
  const payload = { StageName };
  if (reason) payload.Closed_Lost_Reason__c = reason;
  if (notes)  payload.Closed_Lost_Notes__c  = notes;
  const res = await fetch(`${BASE}/api/opportunities/${encodeURIComponent(oppId)}/stage`, {
    method: 'PATCH', headers: getHeaders(), body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// The "how many did we lose on price" query. `months` windows on close date;
// omit for all time. Response carries coveragePct/unspecified — render those,
// not just the split, or a 90%-empty column reads as a confident finding.
export async function fetchLostReasons({ months = null } = {}) {
  const qs = months ? `?months=${encodeURIComponent(months)}` : '';
  const res = await fetch(`${BASE}/api/analytics/lost-reasons${qs}`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Rep-driven (CRM-native) checkout ──────────────────────────────────────
export async function repCheckoutIntent(cartId, payload) {
  const res = await fetch(`${BASE}/api/carts/${encodeURIComponent(cartId)}/rep-checkout/intent`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(payload || {}),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}
export async function repCheckoutComplete(cartId, payload) {
  const res = await fetch(`${BASE}/api/carts/${encodeURIComponent(cartId)}/rep-checkout/complete`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(payload || {}),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Billing status (per-contact Stripe payment methods, aggregated) ───────
export async function getAccountBillingStatus(accountId) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/billing-status`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Email config (Assistant Config sibling) ───────────────────────────────
export async function listEmailTemplates() {
  const res = await fetch(`${BASE}/api/email/templates`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function previewEmail(kind, overrides) {
  const res = await fetch(`${BASE}/api/email/preview`, {
    method: 'POST',
    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind, overrides }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function testSendEmail({ kind, to, overrides }) {
  const res = await fetch(`${BASE}/api/email/test-send`, {
    method: 'POST',
    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind, to, overrides }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function listEmailLogs({ kind, to, limit = 50 } = {}) {
  const p = new URLSearchParams();
  if (kind) p.set('kind', kind);
  if (to)   p.set('to', to);
  p.set('limit', String(limit));
  const res = await fetch(`${BASE}/api/email/logs?${p.toString()}`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Transactions + Refunds ─────────────────────────────────────────────────
export async function listAccountCharges(accountId, { limit = 25 } = {}) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/charges?limit=${limit}`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// `lines` — [{ lineId | orderItemId, amountCents }] summing to amountCents —
// names which Order lines the money returns against (src/lib/refundLines.js).
// Sent only when given; without it the refund is against the whole payment and
// the request is what it always was.
export async function issueRefund({ paymentIntentId, amountCents, reason, note, customerEmail, approvedByEmail, lines }) {
  const res = await fetch(`${BASE}/api/billing/refund`, {
    method: 'POST',
    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      paymentIntentId, amountCents, reason, note, customerEmail, approvedByEmail,
      ...(Array.isArray(lines) && lines.length ? { lines } : {}),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function listAccountBillingActions(accountId) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/billing-actions`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── People (SF Contacts + Firebase-registered users, merged) ───────────────
export async function listAccountPeople(accountId) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/users`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body.people || [];
}

// ─── Events (unified account timeline) ─────────────────────────────────────
export async function listAccountEvents(accountId, { limit = 200 } = {}) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/events?limit=${limit}`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// The stored copy of one email we sent this account — what the customer
// received, with any sign-in secret (reset link, code, password) cut out by
// squish-backend. 404 when the email was not addressed to this account.
export async function getAccountEmail(accountId, logId) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/emails/${encodeURIComponent(logId)}`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body.email;
}

// Customer Product Adapter aggregator — returns every customer-owned
// product across all services (email today; hosting + domains + future
// products as they adopt the adapter).
//
// See squish-docs/architecture/customer-product-adapter.md.
export async function listAccountProducts(accountId) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/products`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// The cart builder's quote for an email tier upgrade, an add-on, or addSeats
// more seats on one of this account's subscriptions, each priced choice with
// its ready cart line (squish-backend lib/staff-email-change
// staffEmailChangeOptions).
export async function getEmailChangeOptions(accountId, subId, { addonSku, seats, addSeats } = {}) {
  const p = new URLSearchParams();
  if (addonSku) p.set('addonSku', addonSku);
  if (seats != null) p.set('seats', String(seats));
  if (addSeats != null) p.set('addSeats', String(addSeats));
  const res = await fetch(`${BASE}/api/accounts/${accountId}/email-subscriptions/${encodeURIComponent(subId)}/cart-options?${p.toString()}`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(body.error || `Request failed (${res.status})`), { code: body.code || null });
  return body;
}

// Rep cancel for an email subscription. Role-gated to admin/support
// at the squish-crm-backend layer.
export async function cancelEmailSubscription(subId, { reason, atPeriodEnd } = {}) {
  const res = await fetch(`${BASE}/api/email-subs/${encodeURIComponent(subId)}/cancel`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ reason, atPeriodEnd }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Support toolkit: live Plesk status + parsed disk/traffic usage for a
// shared hosting site (read-only, any staff role). VPS rows come back 409
// with a pointer at the VPS status/monitoring surfaces.
// Registry + registrar + live DNS for one domain. Each of the three sections
// carries its own `available` flag — render an unavailable one as an error, not
// as emptiness.
export async function getDomainDetail(domain) {
  const res = await fetch(`${BASE}/api/domains/${encodeURIComponent(domain)}/detail`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function getHostingSupportInfo(domain) {
  const res = await fetch(`${BASE}/api/hosting-subs/${encodeURIComponent(domain)}/support-info`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Reveal the Plesk admin password for a Managed Cloud (VPS) site.
//
// Deliberately not a plain read. The CRM gates on role; squish-backend
// resolves the caller against Salesforce and writes a durable audit row
// naming who read it, why, and the free-text note — and refuses to answer if
// that row cannot be written. Shared hosting has no per-site admin password
// and comes back 409 with a pointer at panel SSO.
//
// Returns { domain, login, password, panelUrl, auditId, recorded }. Errors
// carry a `code` so the caller can branch on the refusal rather than on its
// wording.
// A customer's VPS as staff see it: droplet, IP, region, size, panel and its
// install state, status, attached domains, DNS-points-here and HTTPS. No
// credentials. Read-only, any staff role.
export async function getHostingServer(domain) {
  const res = await fetch(`${BASE}/api/hosting-subs/${encodeURIComponent(domain)}/server`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function revealHostingAdminCredential(domain, { reason, note } = {}) {
  const res = await fetch(`${BASE}/api/hosting-subs/${encodeURIComponent(domain)}/admin-credential`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ reason, note }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.code = body.code || null;
    err.status = res.status;
    throw err;
  }
  return body;
}

// Rep cancel for a hosting subscription. Same role-gating as email-subs.
// atPeriodEnd defaults to false at the rep layer (immediate cancel) — flip
// to true for scheduled cancel. Refunds remain CRM-mediated via the
// existing /api/billing/refund flow.
export async function cancelHostingSubscription(domain, { reason, atPeriodEnd } = {}) {
  const res = await fetch(`${BASE}/api/hosting-subs/${encodeURIComponent(domain)}/cancel`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ reason, atPeriodEnd }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Rep cancel for a domain. Domains have no recurring sub + NameSilo has no
// cancel API, so this disables auto-renew (domain lapses at term end). No
// atPeriodEnd — a domain can't be stopped immediately.
export async function cancelDomainSubscription(domain, { reason } = {}) {
  const res = await fetch(`${BASE}/api/domain-subs/${encodeURIComponent(domain)}/cancel`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ reason }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Rep cancel for a paid-but-unprovisioned purchase (stuck email/hosting setup).
// Marks it cancelled — no money moves; refund separately via the Refund button.
export async function cancelPendingSetup(kind, setupId, { reason } = {}) {
  const res = await fetch(`${BASE}/api/pending-setups/${encodeURIComponent(kind)}/${encodeURIComponent(setupId)}/cancel`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ reason }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Cases (the case-driven work model — Phase 1) ──────────────────────────
// Open cases for an account, for the "attach to a case" picker on rep actions.
export async function listAccountCases(accountId) {
  const res = await fetch(`${BASE}/api/accounts/${encodeURIComponent(accountId)}/cases`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body.cases || [];
}

// Create a work-anchor Case (lightweight work-type prefixed into the subject).
// `visibility` routes it through squish-crm-backend lib/staff-case.js — a work
// anchor is 'staff' (silent): without it the route links no contact and flags
// nothing, which reads as an orphan.
export async function createAccountCase(accountId, { subject, workType = null, priority = 'Medium', contactId = null, visibility } = {}) {
  const res = await fetch(`${BASE}/api/accounts/${encodeURIComponent(accountId)}/cases`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ subject, workType, priority, contactId, ...(visibility ? { visibility } : {}) }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return { caseId: body.caseId, caseNumber: body.caseNumber || null };
}

// Errors from the staff-ticket routes carry a code, and CONTACT_REQUIRED
// carries the account's contacts so the form can ask which one.
async function staffCaseCall(url, payload) {
  const res = await fetch(url, { method: 'POST', headers: getHeaders(), body: JSON.stringify(payload) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.code = body.code || null;
    err.contacts = body.contacts || null;
    throw err;
  }
  return body;
}

// Open a ticket on the customer's behalf. Body from lib/caseVisibility
// newTicketBody: visibility 'customer' (linked, emailed the ticket number) or
// 'staff' (silent). → { caseId, caseNumber, visibility, contact, emailed, to|reason }
export function createStaffTicket(accountId, payload) {
  return staffCaseCall(`${BASE}/api/accounts/${encodeURIComponent(accountId)}/cases`, payload);
}

// Link an unlinked case to the customer, or "Tell customer" on a silent one.
// notify:true emails them the ticket number. → { contact, wasSilent, emailed, ... }
export function linkCaseToCustomer(caseId, { notify, contactId = null }) {
  return staffCaseCall(`${BASE}/api/cases/${encodeURIComponent(caseId)}/link-customer`, {
    notify: notify === true, ...(contactId ? { contactId } : {}),
  });
}

// Append an internal comment to a Case (used to anchor a rep action to its case).
export async function addCaseComment(caseId, commentBody) {
  const res = await fetch(`${BASE}/api/cases/${encodeURIComponent(caseId)}/comments`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ body: commentBody, isPublic: false }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Vendor State Reconciler — on-demand single-record reconciliation triggered
// from the Subscriptions panel "Reconcile now" button. Returns the drift
// descriptor (or null if vendor matches internal). See:
//   squish-docs/architecture/vendor-state-reconciler.md
export async function reconcileSubscription(productKind, productId) {
  const res = await fetch(`${BASE}/api/reconcile/${encodeURIComponent(productKind)}/${encodeURIComponent(productId)}`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({}),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Impersonation ──────────────────────────────────────────────────────────
export async function startImpersonation(accountId, { targetUid, reason, ttlMs } = {}) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/impersonation/start`, {
    method: 'POST', headers: getHeaders(),
    body: JSON.stringify({ targetUid, reason, ttlMs }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Staff-driven removal of a customer's second factor — admin-only, typed
// reason, audit-as-precondition server-side (POST /api/mfa/remove wears the
// OTP override's fail-closed shape). squish-backend removes the factors,
// revokes sessions and emails the "wasn't you?" notice.
export async function removeCustomerMfa(accountId, { email, reason } = {}) {
  const res = await fetch(`${BASE}/api/mfa/remove`, {
    method: 'POST', headers: getHeaders(),
    body: JSON.stringify({ accountId, email, reason }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Self-serve account deletions, admin side (squish-crm-backend
// /api/admin/account-deletions, admin only). Approving runs squish-backend's
// erasure, which cannot be undone: the server checks the typed email against
// the request before it acts. Errors carry the status and the server's code
// (ERASURE_DISABLED, REFUSED, INCOMPLETE, NOT_AWAITING_APPROVAL).
export async function listAccountDeletions() {
  const res = await fetch(`${BASE}/api/admin/account-deletions`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body.requests || [];
}

export async function approveAccountDeletion(uid, { confirmEmail } = {}) {
  const res = await fetch(`${BASE}/api/admin/account-deletions/${encodeURIComponent(uid)}/approve`, {
    method: 'POST', headers: getHeaders(),
    body: JSON.stringify({ confirmEmail }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = body.code || null;
    throw err;
  }
  return body;
}

export async function endImpersonation(sessionId, { reason } = {}) {
  const res = await fetch(`${BASE}/api/impersonation/${sessionId}/end`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify({ reason }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export async function listAccountImpersonation(accountId) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/impersonation`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body.sessions || [];
}

export async function getImpersonationDetail(sessionId) {
  const res = await fetch(`${BASE}/api/impersonation/${sessionId}`, {
    headers: getHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Account profile editing ────────────────────────────────────────────────
export async function updateAccountProfile(accountId, patch) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/profile`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(patch),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}
// ─── Conversations (read side of chat-capture pipeline) ──────────────────────
// Phase 3 backend exposes /api/conversations + /api/conversations/:id +
// POST /api/conversations/:id/escalate. These helpers wrap them with the
// same getHeaders + error-shape conventions used elsewhere in this file.

export async function fetchConversations(filters = {}) {
  const params = new URLSearchParams();
  if (filters.source)    params.set('source', filters.source);
  if (filters.intent)    params.set('intent', filters.intent);
  if (filters.accountId) params.set('accountId', filters.accountId);
  if (filters.since)     params.set('since', filters.since);
  if (filters.limit)     params.set('limit', String(filters.limit));
  if (filters.offset)    params.set('offset', String(filters.offset));
  const qs = params.toString();
  const res = await fetch(`${BASE}/api/conversations${qs ? '?' + qs : ''}`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Failed to fetch conversations');
  return res.json(); // { conversations: [...], paging }
}

export async function getConversation(id) {
  const res = await fetch(`${BASE}/api/conversations/${encodeURIComponent(id)}`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Failed to fetch conversation');
  return res.json(); // { conversation, messages }
}

export async function escalateConversation(id, { subject, description, priority }) {
  const res = await fetch(`${BASE}/api/conversations/${encodeURIComponent(id)}/escalate`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ subject, description, priority }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body; // { success: true, caseId } or 409 { error: 'Already escalated', caseId }
}

// ─── Referral bounty program ────────────────────────────────────────────────
export async function fetchReferralSummary() {
  const res = await fetch(`${BASE}/api/referrals/summary`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body; // { summary: [...], totalReferredSignups }
}

export async function createReferralCode({ code, partnerName, email, bounty, notes }) {
  const res = await fetch(`${BASE}/api/referrals/codes`, {
    method: 'POST', headers: getHeaders(),
    body: JSON.stringify({ code, partnerName, email, bounty, notes }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body; // { success, id, code }
}

export async function updateReferralCode(id, patch) {
  const res = await fetch(`${BASE}/api/referrals/codes/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: getHeaders(), body: JSON.stringify(patch),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// ─── Live product catalogs (canonical code catalogs in squish-backend) ──────
// Aggregate of the public /api/catalog/* projections — hosting tiers, hosting
// add-ons, email tiers, email add-ons, domain TLD floors, billing terms.
// Read-only: prices change in the squish-backend repo (+ reprice scripts),
// never from the CRM. Renders the Products page.
export async function fetchLiveProductCatalog() {
  const res = await fetch(`${BASE}/api/products/live`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body; // { hosting, hostingAddons, email, emailAddons, domains, billingTerms, errors }
}

// Full NameSilo TLD price sheet (hundreds of TLDs, retail + our cost).
// Server-cached 6h; fetched separately from the main catalog because the
// upstream NameSilo call is slow and occasionally flaky.
export async function fetchTldPrices() {
  const res = await fetch(`${BASE}/api/products/tld-prices`, { headers: getHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body; // { prices: { com: { registration, renewal, transfer, cost }, ... }, cachedAt, stale? }
}

// ─── The ~ console (agentic surface) ────────────────────────────────────────
// `context` is what the mounted pages say is on screen (lib/viewContext) — the
// agent uses it to resolve "this account" / "this ticket" without a lookup.
//
// Streams Server-Sent Events: `onEvent` fires for every thinking token, tool
// dispatch and answer token as the agent works, and the promise resolves once
// the run is done. Pass `signal` to cancel in flight.
export async function consoleStream(messages, context, onEvent, signal) {
  const res = await fetch(`${BASE}/api/console/stream`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ messages, context: context || undefined }),
    signal,
  });
  if (!res.ok) {
    // Errors before the stream opens are still plain JSON (503 no key, 400 bad body).
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by a blank line; the tail is a partial frame.
    const frames = buffer.split('\n\n');
    buffer = frames.pop();
    for (const frame of frames) {
      const data = frame.split('\n').filter(l => l.startsWith('data:')).map(l => l.slice(5).trim()).join('');
      if (!data) continue; // heartbeat comment frame
      let evt;
      try { evt = JSON.parse(data); } catch { continue; }
      if (evt.type === 'error') throw new Error(evt.error || 'Console command failed');
      onEvent(evt);
    }
  }
}

// Open the case a rep uses to say hello to a new account first. Creates the
// Case only: the hello itself is a public comment the rep writes and sends
// from the ticket workspace, so nothing reaches the customer here. Returns
// { caseId, caseNumber, existing } — `existing: true` means this account
// already had one open and we are going back to it rather than opening a
// second.
export async function openOutreachCase(accountId, { contactId = null, taskId = null, subject } = {}) {
  const res = await fetch(`${BASE}/api/accounts/${accountId}/outreach-case`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ contactId, taskId, ...(subject ? { subject } : {}) }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Could not open the conversation (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}

// ─── Requests (@genius in Slack → internal request cards) ──────────────────
// Staff file an idea, a bug or a build request by mentioning @genius in Slack;
// squish-crm-backend turns each mention into a card. Internal only — nothing
// here is a customer ticket.
//
// These routes answer errors as { error: <code>, message: <human text> }, the
// other way round from most of this file, so `message` is what gets thrown
// and the code rides along as err.code. err.body is the whole answer: a 409
// on claim names the current owner there (also lifted to err.owner), which is
// what the page's "Take over" offer is built from.
async function requestsFetch(path, init = {}) {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: getHeaders(init.headers) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = typeof body.error === 'string' ? body.error : null;
    // `unavailable` (a 502 from squish-crm-backend) is the service behind
    // this one being down, not anything the staffer did.
    const fallback = res.status === 503 || code === 'unavailable'
      ? 'Requests are unavailable right now (the service behind them is not answering)'
      : (code || `Request failed (${res.status})`);
    const err = new Error(body.message || fallback);
    err.status = res.status;
    err.code = code;
    err.body = body;
    err.owner = body.owner || null;
    throw err;
  }
  return body;
}

// Every open and claimed card, plus the 50 most recently closed.
// → { requests, counts: { open, claimed, mine, closed }, me: { email } }
export function fetchRequests() {
  return requestsFetch('/api/requests');
}

// The mutations each answer { request } — the card as it now stands.
const requestAction = (id, action, payload = {}) =>
  requestsFetch(`/api/requests/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then(body => body.request || null);

// Assign the card to the caller. If someone else already has it the server
// answers 409 already_claimed with their name; `force: true` takes it anyway.
export function claimRequest(id, { force = false } = {}) {
  return requestAction(id, 'claim', force ? { force: true } : {});
}

// Back to unassigned.
export function releaseRequest(id) {
  return requestAction(id, 'release');
}

// An internal note on the card. Never sent to Slack.
export function addRequestNote(id, text) {
  return requestAction(id, 'notes', { text });
}

// OUTWARD-FACING: `note` is posted to the Slack thread and DMed to the poster.
// outcome is 'done' | 'wont_do' | 'duplicate'; the note is required for all three.
export function closeRequest(id, { outcome, note } = {}) {
  return requestAction(id, 'close', { outcome, note });
}

// An image off a card: a screenshot that was on the Slack ask. Bytes, not
// JSON, so it does not go through requestsFetch; an error still answers JSON
// with the human sentence in `message`. Cached per image for the tab's life
// (a settled { url } or the in-flight promise), so reopening a card does not
// fetch it again.
const requestImageCache = new Map();
export async function fetchRequestImage(id, fileId) {
  const key = `${id}/${fileId}`;
  if (requestImageCache.has(key)) return requestImageCache.get(key);
  const p = (async () => {
    const res = await fetch(`${BASE}/api/requests/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`, { headers: getHeaders() });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Could not load the image');
    }
    return { url: URL.createObjectURL(await res.blob()) };
  })();
  requestImageCache.set(key, p);
  try {
    const out = await p;
    requestImageCache.set(key, out);
    return out;
  } catch (e) {
    requestImageCache.delete(key);
    throw e;
  }
}

// Closed → claimed by the caller.
export function reopenRequest(id) {
  return requestAction(id, 'reopen');
}

// ─── Finance (Casey only) ───────────────────────────────────────────────────
// The management view of the money: MRR, the monthly P&L, cash, vendor spend
// and the irregularity findings. squish-crm-backend gates every /api/finance
// route on the admin role AND an email allowlist; anyone else gets a 403,
// which pages/Finance.jsx turns into one plain sentence. Amounts are integer
// cents. Same error shape as dnsFetch: Error.status carries the HTTP code and
// the message is the server's own `error` (a 400 on a vendor rule says what
// was wrong with it).
async function financeFetch(path, init = {}) {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: getHeaders(init.headers) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export function fetchFinanceOverview() {
  return financeFetch('/api/finance/overview');
}

// kind = 'weekly' (key = the Monday, YYYY-MM-DD) or 'monthly' (key = YYYY-MM).
// 404 when no such report was ever computed.
export function fetchFinanceReport(kind, key) {
  return financeFetch(`/api/finance/report/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`);
}

// Newest first. Every filter is optional: from, to, category, vendorKey, q,
// direction ('in' | 'out'), limit.
export function fetchFinanceTransactions(filters = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v != null && v !== '') qs.set(k, String(v));
  }
  const q = qs.toString();
  return financeFetch(`/api/finance/transactions${q ? `?${q}` : ''}`);
}

export function fetchFinanceVendors() {
  return financeFetch('/api/finance/vendors');
}

// Classify ONE bank row by hand; 'auto' hands it back to the vendor rules.
export function setFinanceTransactionCategory(id, category) {
  return financeFetch(`/api/finance/transactions/${encodeURIComponent(id)}/category`, {
    method: 'POST',
    body: JSON.stringify({ category }),
  });
}

// "Classify once; the rule then sticks": creates or updates a vendor rule and
// re-categorises every stored row it matches.
export function saveFinanceVendorRule({ key, name, category, namePatterns, direction = null }) {
  return financeFetch('/api/finance/vendor-rules', {
    method: 'POST',
    body: JSON.stringify({ key, name, category, namePatterns, direction }),
  });
}

// action = 'acknowledge' | 'resolve' | 'reopen'; the note is optional (≤500).
export function actOnFinanceFinding(id, { action, note } = {}) {
  return financeFetch(`/api/finance/findings/${encodeURIComponent(id)}`, {
    method: 'POST',
    body: JSON.stringify(note ? { action, note } : { action }),
  });
}
